
import { useState, useEffect, useRef, useCallback } from "react"
import { MessageSquare, Phone } from "lucide-react"
import { Button, Alert, AlertTitle, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material"
import ChatImg from "../../assets/chatImg.jpg"
import useDynamicPhoneNumber from "../hooks/useDynamicPhoneNumber"
import { sendLandingPageFormEmail,sendToLeadProsper } from './email'


    let initialLandingUrl = null;

  const getSourceUrl = () => {
        if (typeof window === "undefined") return "Unknown";

        // If we haven't stored the initial URL yet, store it
        if (!initialLandingUrl) {
            initialLandingUrl = window.location.href;
        }

        return initialLandingUrl;
    };
    const getIPAddress = async () => {
        try {
            const response = await fetch("https://api.ipify.org?format=json");
            const data = await response.json();
            return data.ip;
        } catch (error) {
            console.error("Failed to get IP address:", error);
            return "IP address not available";
        }
    };
// Text content object for reusability
const TEXT_CONTENT = {
  ui: {
    onlineStatus: "Claire is online",
    verificationTitle: "Verify You Are Not a Robot",
    submitting: "Submitting...",
    submit: "Submit"
  },

  chat: {
    claire: "Claire",
    initialMessages: [
      "Hi 👋",
      "I’m from Connect With Attorney.",
      "Many women are now coming forward after being diagnosed with ovarian cancer linked to long-term talcum powder use.",
      "Would you like to check whether you may qualify for compensation?"
    ],

    diagnosisIntro: "Great. I just need to ask you one quick question to get started.",
    diagnosisQuestion: "Have you been diagnosed with ovarian cancer or fallopian tube cancer?",

    qualified: {
      intro: "Thank you. You may qualify for a claim review.",
      details:
        "Women who used Johnson’s Baby Powder or Shower to Shower for personal hygiene and were later diagnosed with ovarian cancer may be entitled to compensation for medical bills, treatments, and other losses — with no upfront costs.",
      callToAction:
        "Tap below to speak with a legal specialist for a free case review.",
      buttonText: "CALL NOW — FREE CASE REVIEW"
    },

    notQualified: {
      intro: "No problem 😊",
      message1:
        "If you ever want to check for yourself or a loved one, our legal team is here to help.",
      message2: "",
      message3: ""
    },

    declined: {
      intro: "Totally understand 👍",
      message1:
        "If you ever want to check for yourself or a loved one, our legal team is here to help.",
      message2: "",
    }
  },

  buttons: {
    yes: "Yes",
    no: "No"
  },

  website: {
    url: "tel:+18882021350",
    displayUrl: "(888) 202-1350"
  },

  form: {
    labels: {
      name: "Name",
      number: "Phone Number",
      email: "Email"
    },
    placeholders: {
      number: "(123) 456-7890"
    },
    errors: {
      nameRequired: "Name is required",
      nameInvalid: "Name should only contain letters and spaces",
      numberRequired: "Phone number is required",
      numberInvalid: "Please enter exactly 10 digits (numbers only)",
      emailRequired: "Email is required",
      emailInvalid: "Please enter a valid email address",
      submitFailed: "Failed to submit form. Please try again.",
      generalError: "An error occurred. Please try again."
    }
  }
}


// Typing indicator component
const TypingIndicator = () => (
  <div className="flex items-start gap-2">
    <div className="bg-gray-700/80 text-white py-2 px-3 rounded-lg rounded-tl-none">
      <div className="flex space-x-1">
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
      </div>
    </div>
  </div>
)

// Chat message component for reusability
const ChatMessage = ({ sender, message, showActions, onYes, onNo, showButton, buttonText, buttonIcon, buttonOnClick }) => (
  <div className="flex items-start gap-2">
    <div className="bg-gray-700/80 text-white py-2 px-3 rounded-lg rounded-tl-none max-w-[80%]">
      <div className="text-xs text-gray-300 mb-1">{sender}</div>
      <p>{message}</p>
      {showActions && (
        <div className="flex gap-2 mt-2">
          <Button variant="contained" color="success" size="small" onClick={onYes}>
            {TEXT_CONTENT.buttons.yes}
          </Button>
          <Button variant="contained" color="error" size="small" onClick={onNo}>
            {TEXT_CONTENT.buttons.no}
          </Button>
        </div>
      )}
      {showButton && (
        <Button
          variant="contained"
          color="primary"
          size="large"
          className="mt-2"
          startIcon={buttonIcon}
          onClick={buttonOnClick}
        >
          {buttonText}
        </Button>
      )}
    </div>
  </div>
)

const UserMessage = ({ message }) => (
  <div className="flex items-start justify-end">
    <div className="bg-green-600/90 text-white py-2 px-3 rounded-lg rounded-tr-none max-w-[80%]">
      <p>{message}</p>
    </div>
  </div>
)

export default function ChatInterface() {
  const chatContainerRef = useRef(null)
  const bottomRef = useRef(null)


  const phoneNumber = "+1 (888) 202-1350"
  
  // State management
  const [chatState, setChatState] = useState({
    showInitialResponse: false,
    showDiagnosisIntro: false,
    showDiagnosisQuestion: false,
    showQualifiedIntro: false,
    showQualifiedDetails: false,
    showQualifiedCall: false,
    showNotQualified: false,
    showNotQualifiedMessages: Array(6).fill(false),
    showDeclined: false,
    showDeclinedMessages: Array(2).fill(false),
    showTyping: true,
    showInitialMessages: Array(4).fill(false)
  })

  const [userResponse, setUserResponse] = useState("")
  const [diagnosisResponse, setDiagnosisResponse] = useState("")
  const [pingUrl, setPingUrl] = useState("")
  const [certId, setCertId] = useState("")
  const [tokenUrl, setTokenUrl] = useState("")
  const [openVerification, setOpenVerification] = useState(false)
  const [verificationForm, setVerificationForm] = useState({
  name: '',
  number: '',
  email: '',
  zipcode: '',
  address: ''
})

const [formErrors, setFormErrors] = useState({
  name: '',
  number: '',
  email: '',
  zipcode: '',
  address: ''
})

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [showSuccessCard, setShowSuccessCard] = useState(false)
  const [errors, setErrors] = useState({})

  // Chat flow configuration
  const chatFlow = {
    initial: [
      { delay: 1300, action: () => updateChatState({ showTyping: false, showInitialMessages: [true, false, false, false] }) },
      { delay: 1600, action: () => updateChatState({ showTyping: true }) },
      { delay: 2900, action: () => updateChatState({ showTyping: false, showInitialMessages: [true, true, false, false] }) },
      { delay: 3400, action: () => updateChatState({ showTyping: true }) },
      { delay: 4900, action: () => updateChatState({ showTyping: false, showInitialMessages: [true, true, true, false] }) },
      { delay: 5000, action: () => updateChatState({ showTyping: true }) },
      { delay: 6500, action: () => updateChatState({ showTyping: false, showInitialMessages: [true, true, true, true] }) }
    ]
  }

  //
  useEffect(() => {
        // Prevent duplicate Google Tag injection
        if (document.getElementById('gtag-talcum')) return;

        const gtagScript = document.createElement('script');
        gtagScript.async = true;
        gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=AW-17927795096';
        gtagScript.id = 'gtag-talcum';

        const inlineScript = document.createElement('script');
        inlineScript.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'AW-17927795096');
        `;

        document.head.appendChild(gtagScript);
        document.head.appendChild(inlineScript);
        }, []);

  // TrustedForm observer
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes" && mutation.attributeName === "value") {
          const certIdVar = mutation.target.value
          const tokenUrlVar = mutation.target.value
          const pingUrlVar = mutation.target.attributes[0]?.ownerDocument?.all?.["xxTrustedFormPingUrl"]?.value

          setCertId(certIdVar)
          setPingUrl(pingUrlVar)
          setTokenUrl(tokenUrlVar)
// console.log("TrustedForm values updated:", { certIdVar, pingUrlVar, tokenUrlVar })
          if (certIdVar) {
            fetchCertData(certIdVar)
          }
        }
      })
    })



    const certField = document.getElementById("xxTrustedFormCertUrl")
    if (certField) {
      observer.observe(certField, { attributes: true })
    }

    return () => observer.disconnect()
  }, [])

  const fetchCertData = async (certUrl) => {
    try {
      const response = await fetch(certUrl)
      const data = await response.json()
      // console.log("TrustedForm Cert Data:", data)
    } catch (error) {
      console.error("Error fetching TrustedForm cert:", error)
    }
  }

  // Scroll utilities
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
    }, 100)
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [chatState, scrollToBottom])

  // Initialize chat flow
  useEffect(() => {
    const timeouts = chatFlow.initial.map(step => 
      setTimeout(step.action, step.delay)
    )

    return () => timeouts.forEach(clearTimeout)
  }, [])

  

  // State management helpers
  const updateChatState = useCallback((updates) => {
    setChatState(prev => ({ ...prev, ...updates }))
  }, [])

  const setTypingSequence = useCallback((callback, delay = 1300) => {
    updateChatState({ showTyping: true })
    setTimeout(() => {
      updateChatState({ showTyping: false })
      callback()
    }, delay)
  }, [updateChatState])

  // Chat handlers
  const handleInitialYes = useCallback(() => {
    setUserResponse("Yes")
    updateChatState({ showInitialResponse: true })
    
    setTypingSequence(() => {
      updateChatState({ showDiagnosisIntro: true })
      setTimeout(() => {
        setTypingSequence(() => {
          updateChatState({ showDiagnosisQuestion: true })
          scrollToBottom()
        })
      }, 1800)
    })
  }, [setTypingSequence, updateChatState, scrollToBottom])

  const handleInitialNo = useCallback(() => {
    setUserResponse("No")
    updateChatState({ showInitialResponse: true })
    
    setTypingSequence(() => {
      updateChatState({ showDeclined: true })
      setTimeout(() => {
        setTypingSequence(() => {
          updateChatState({ showDeclinedMessages: [true, false] })
          setTimeout(() => {
            setTypingSequence(() => {
              updateChatState({ showDeclinedMessages: [true, true] })
              scrollToBottom()
            })
          }, 1800)
        })
      }, 1800)
    })
  }, [setTypingSequence, updateChatState, scrollToBottom])

  const handleDiagnosisYes = useCallback(() => {
    setDiagnosisResponse("Yes")
    
    setTypingSequence(() => {
      updateChatState({ showQualifiedIntro: true })
      setTimeout(() => {
        setTypingSequence(() => {
          updateChatState({ showQualifiedDetails: true })
          setTimeout(() => {
            setTypingSequence(() => {
              updateChatState({ showQualifiedCall: true })
              scrollToBottom()
            })
          }, 1800)
        })
      }, 1800)
    })
  }, [setTypingSequence, updateChatState, scrollToBottom])

  const handleDiagnosisNo = useCallback(() => {
    setDiagnosisResponse("No")
    
    setTypingSequence(() => {
      updateChatState({ showNotQualified: true })
      const messagesSequence = Array(6).fill(false).map((_, index) => 
        setTimeout(() => {
          setTypingSequence(() => {
            updateChatState({ 
              showNotQualifiedMessages: Array(6).fill(false).map((_, i) => i <= index)
            })
            if (index === 5) scrollToBottom()
          })
        }, index * 1800)
      )
    })
  }, [setTypingSequence, updateChatState, scrollToBottom])

  // Form handlers
  const handleVerificationOpen = () => setOpenVerification(true)
  const handleVerificationClose = () => setOpenVerification(false)

  const validateForm = useCallback(() => {
    const newErrors = {
      name: '',
      number: '',
      email: ''
    }
    let isValid = true

    // Name validation
    const nameRegex = /^[A-Za-z\s]{2,}$/
    if (!verificationForm.name.trim()) {
      newErrors.name = TEXT_CONTENT.form.errors.nameRequired
      isValid = false
    } else if (!nameRegex.test(verificationForm.name.trim())) {
      newErrors.name = TEXT_CONTENT.form.errors.nameInvalid
      isValid = false
    }

    // Phone validation
    const phoneRegex = /^\d{10}$/
    if (!verificationForm.number.trim()) {
      newErrors.number = TEXT_CONTENT.form.errors.numberRequired
      isValid = false
    } else if (!phoneRegex.test(verificationForm.number.replace(/\D/g, ''))) {
      newErrors.number = TEXT_CONTENT.form.errors.numberInvalid
      isValid = false
    }

    // Email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    if (!verificationForm.email.trim()) {
      newErrors.email = TEXT_CONTENT.form.errors.emailRequired
      isValid = false
    } else if (!emailRegex.test(verificationForm.email)) {
      newErrors.email = TEXT_CONTENT.form.errors.emailInvalid
      isValid = false
    }

    // ZIP Code validation (5 digits)
const zipRegex = /^\d{5}$/
if (!verificationForm.zipcode.trim()) {
  newErrors.zipcode = "ZIP Code is required"
  isValid = false
} else if (!zipRegex.test(verificationForm.zipcode)) {
  newErrors.zipcode = "Enter valid 5-digit ZIP Code"
  isValid = false
}

// Address validation
if (!verificationForm.address.trim()) {
  newErrors.address = "Address is required"
  isValid = false
}


    setFormErrors(newErrors)
    return isValid
  }, [verificationForm])
    // --- SAVE TO CRM ---
    const saveToCRM = async ({ payload }) => {
      try {
        const ipAddress = await getIPAddress();

        await fetch(
          "https://crm-internal-backend-ayb9fqawg8b6bjen.canadacentral-01.azurewebsites.net/api/submitformdata",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              countryName: "USA",
              brandName: "Project 6",
              websiteName: "Connect With Attorney",
              formname: "Talcum Powder Lawsuit 001",
              data: {
                submissionDate: new Date().toLocaleString(),
                FirstName: payload.name,
                LastName: payload.LastName,
                email: payload.email,
                phoneNumber: payload.number,
                zip: payload.zipcode,
                q1: payload["Are you female?"],
                q2: payload["Were you diagnosed with ovarian cancer or fallopian tube cancer in 2000 or later?"],
                q3: payload["Did you regularly use Johnson's Baby Powder or Johnson & Johnson's Shower to Shower for personal hygiene ?"],
                q4: payload["Did your use occur after 1982?"],
                q5: payload["For how long did you use Johnson's Baby Powder or Shower to Shower for personal hygiene?"],
                q6: payload["Are you currently represented by an attorney for this case?"],
                trustedFormCertUrl: certId,
                trustedFormPingUrl: pingUrl,
                trustedFormToken: tokenUrl,
                ip_address: ipAddress,
                page_source: getSourceUrl(),
              },
            }),
          }
        );

        return true;
      } catch (e) {
        console.log(e);
        return false;
      }
    };
  const handleVerificationSubmit = async () => {
  if (!validateForm()) return;
// console.log("Submitting form with data:", verificationForm);
  setIsSubmitting(true);

  try {
    // --- SEND EMAIL ---
    const response = await sendLandingPageFormEmail(
      {
        fullName: verificationForm.name,
        email: verificationForm.email,
        phone: verificationForm.number,
        countryCode: "+1",
        zipcode: verificationForm.zipcode,
        address: verificationForm.address,
      },
      {
        xxTrustedFormCertUrl: certId,
        xxTrustedFormPingUrl: pingUrl,
        xxTrustedFormCertToken: tokenUrl,
      }
    );

    // --- SEND TO LEADPROSPER ---
    const leadprosperResponse = await sendToLeadProsper(
      {
        fullName: verificationForm.name,
        email: verificationForm.email,
        phone: verificationForm.number,
        countryCode: "+1",
        zipcode: verificationForm.zipcode,
        address: verificationForm.address,
      },
      {
        xxTrustedFormCertUrl: certId,
        xxTrustedFormPingUrl: pingUrl,
        xxTrustedFormCertToken: tokenUrl,
      }
    );
    // console.log("LeadProsper response:", leadprosperResponse);
await saveToCRM({ payload: verificationForm })


    // ONLY RUN SUCCESS FLOW IF EMAIL SUCCESS
    if (response.success) {
      setFormSubmitted(true);
      setShowSuccessCard(true);
      setVerificationForm({ name: "", number: "", email: "" });

      handleVerificationClose();

      // --- FIXED PHONE REDIRECT ---
      window.location.href = `tel:${phoneNumber.replace(/\D/g, "")}`;
    } else {
      setErrors((prev) => ({
        ...prev,
        submit: TEXT_CONTENT.form.errors.submitFailed,
      }));
    }
  } catch (error) {
    console.error("Error submitting form:", error);

    setErrors((prev) => ({
      ...prev,
      submit: TEXT_CONTENT.form.errors.generalError,
    }));
  } finally {
    setIsSubmitting(false);
  }
};


  const handleInputChange = (e) => {
    const { name, value } = e.target
    let processedValue = value

    if (name === 'number') {
      processedValue = value.replace(/\D/g, '').slice(0, 10)
    }
if (name === 'zipcode') {
  processedValue = value.replace(/\D/g, '').slice(0, 5)
}

    setVerificationForm(prev => ({
      ...prev,
      [name]: processedValue
    }))
    
    setFormErrors(prev => ({
      ...prev,
      [name]: ''
    }))
  }

  // Helper function to render website link
  const renderWebsiteLink = (text) => (
    <>
      {text}
      <a 
        href={TEXT_CONTENT.website.url} 
        className="text-blue-400 hover:underline" 
        target="_blank" 
        rel="noopener noreferrer"
      >
        {TEXT_CONTENT.website.displayUrl}
      </a>
    </>
  )

  return (
   <div
  className="
    relative min-h-screen w-full flex flex-col bg-cover bg-center p-4
    md:p-6 lg:p-8 xl:p-10 2xl:p-12 3xl:p-16
  "
  style={{ backgroundImage: `url(${ChatImg})` }}
>
  <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

  <form>
    <input type="hidden" id="xxTrustedFormCertUrl" name="xxTrustedFormCertUrl" value={certId} />
    <input type="hidden" id="xxTrustedFormCertToken" name="xxTrustedFormCertToken" value={tokenUrl} />
    <input type="hidden" id="xxTrustedFormPingUrl" name="xxTrustedFormPingUrl" value={pingUrl} />
  </form>

  <div
    className="
      relative z-10 flex flex-col h-full max-w-md mx-auto w-full
      sm:max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl
      2xl:max-w-4xl 3xl:max-w-5xl 4xl:max-w-6xl
    "
  >
    {/* Online Status */}
    <div className="flex items-center gap-2 mb-4 bg-black/30 p-3 rounded-lg lg:p-4 xl:p-5 2xl:p-6">
      <div className="relative">
        <div className="h-3 w-3 bg-green-500 rounded-full"></div>
        <div className="absolute top-0 left-0 h-3 w-3 bg-green-500 rounded-full animate-ping opacity-75"></div>
      </div>
      <span className="text-white font-medium text-sm md:text-base lg:text-lg xl:text-xl">
        {TEXT_CONTENT.ui.onlineStatus}
      </span>
    </div>

    {/* Chat Messages */}
    <div
      ref={chatContainerRef}
      className="
        flex-1 overflow-y-auto space-y-4 mb-4
        h-[calc(100vh-200px)]
        lg:h-[calc(100vh-250px)]
        xl:h-[calc(100vh-300px)]
        2xl:h-[calc(100vh-350px)]
        3xl:h-[calc(100vh-400px)]
      "
    >
      {/* Initial messages */}
      {chatState.showTyping && !chatState.showInitialMessages[0] && <TypingIndicator />}

      {chatState.showInitialMessages[0] && (
        <ChatMessage sender={TEXT_CONTENT.chat.claire} message={TEXT_CONTENT.chat.initialMessages[0]} />
      )}

      {chatState.showTyping && chatState.showInitialMessages[0] && !chatState.showInitialMessages[1] && <TypingIndicator />}
      {chatState.showInitialMessages[1] && (
        <ChatMessage sender={TEXT_CONTENT.chat.claire} message={TEXT_CONTENT.chat.initialMessages[1]} />
      )}

      {chatState.showTyping && chatState.showInitialMessages[1] && !chatState.showInitialMessages[2] && <TypingIndicator />}
      {chatState.showInitialMessages[2] && (
        <ChatMessage sender={TEXT_CONTENT.chat.claire} message={TEXT_CONTENT.chat.initialMessages[2]} />
      )}

      {chatState.showTyping && chatState.showInitialMessages[2] && !chatState.showInitialMessages[3] && <TypingIndicator />}
      {chatState.showInitialMessages[3] && (
        <ChatMessage
          sender={TEXT_CONTENT.chat.claire}
          message={TEXT_CONTENT.chat.initialMessages[3]}
          showActions={!chatState.showInitialResponse}
          onYes={handleInitialYes}
          onNo={handleInitialNo}
        />
      )}

      {/* Initial Response */}
      {chatState.showInitialResponse && <UserMessage message={userResponse} />}

      {/* Diagnosis Flow */}
      {chatState.showTyping &&
        chatState.showInitialResponse &&
        !chatState.showDiagnosisIntro &&
        !chatState.showDiagnosisQuestion &&
        !chatState.showDeclined && <TypingIndicator />}

      {chatState.showDiagnosisIntro && (
        <ChatMessage sender={TEXT_CONTENT.chat.claire} message={TEXT_CONTENT.chat.diagnosisIntro} />
      )}

      {chatState.showTyping &&
        chatState.showDiagnosisIntro &&
        !chatState.showDiagnosisQuestion &&
        !chatState.showDeclined && <TypingIndicator />}

      {chatState.showDiagnosisQuestion && (
        <ChatMessage
          sender={TEXT_CONTENT.chat.claire}
          message={TEXT_CONTENT.chat.diagnosisQuestion}
          showActions={!chatState.showQualifiedIntro && !chatState.showNotQualified}
          onYes={handleDiagnosisYes}
          onNo={handleDiagnosisNo}
        />
      )}

      {/* Diagnosis Response */}
      {diagnosisResponse && <UserMessage message={diagnosisResponse} />}

      {/* Qualified Flow */}
      {chatState.showTyping &&
        chatState.showDiagnosisQuestion &&
        !chatState.showQualifiedIntro &&
        !chatState.showNotQualified && <TypingIndicator />}

      {chatState.showQualifiedIntro && (
        <ChatMessage sender={TEXT_CONTENT.chat.claire} message={TEXT_CONTENT.chat.qualified.intro} />
      )}

      {chatState.showTyping &&
        chatState.showQualifiedIntro &&
        !chatState.showQualifiedDetails &&
        !chatState.showNotQualified && <TypingIndicator />}

      {chatState.showQualifiedDetails && (
        <ChatMessage sender={TEXT_CONTENT.chat.claire} message={TEXT_CONTENT.chat.qualified.details} />
      )}

      {chatState.showTyping &&
        chatState.showQualifiedDetails &&
        !chatState.showQualifiedCall &&
        !chatState.showNotQualified && <TypingIndicator />}

      {chatState.showQualifiedCall && (
        <ChatMessage
          sender={TEXT_CONTENT.chat.claire}
          message={TEXT_CONTENT.chat.qualified.callToAction}
          showButton={true}
          buttonText={`${TEXT_CONTENT.chat.qualified.buttonText} ${phoneNumber}`}
          buttonIcon={<Phone />}
          buttonOnClick={handleVerificationOpen}
        />
      )}

      {/* Not Qualified Flow */}
      {chatState.showNotQualified && (
        <ChatMessage sender={TEXT_CONTENT.chat.claire} message={TEXT_CONTENT.chat.notQualified.intro} />
      )}

      {chatState.showTyping &&
        chatState.showNotQualified &&
        !chatState.showNotQualifiedMessages[0] && <TypingIndicator />}

      {chatState.showNotQualifiedMessages[0] && (
        <ChatMessage sender={TEXT_CONTENT.chat.claire} message={TEXT_CONTENT.chat.notQualified.message1} />
      )}

      {chatState.showTyping &&
        chatState.showNotQualifiedMessages[0] &&
        !chatState.showNotQualifiedMessages[1] && <TypingIndicator />}

      {chatState.showNotQualifiedMessages[1] && (
        <ChatMessage
          sender={TEXT_CONTENT.chat.claire}
          message={renderWebsiteLink(TEXT_CONTENT.chat.notQualified.message2)}
        />
      )}

      {chatState.showTyping &&
        chatState.showNotQualifiedMessages[1] &&
        !chatState.showNotQualifiedMessages[2] && <TypingIndicator />}

      {chatState.showNotQualifiedMessages[2] && (
        <ChatMessage sender={TEXT_CONTENT.chat.claire} message={TEXT_CONTENT.chat.notQualified.message3} />
      )}

      {/* Declined */}
      {chatState.showDeclined && (
        <ChatMessage sender={TEXT_CONTENT.chat.claire} message={TEXT_CONTENT.chat.declined.intro} />
      )}

      {chatState.showTyping &&
        chatState.showDeclined &&
        !chatState.showDeclinedMessages[0] && <TypingIndicator />}

      {chatState.showDeclinedMessages[0] && (
        <ChatMessage
          sender={TEXT_CONTENT.chat.claire}
          message={renderWebsiteLink(TEXT_CONTENT.chat.declined.message1)}
        />
      )}

      {chatState.showTyping &&
        chatState.showDeclinedMessages[0] &&
        !chatState.showDeclinedMessages[1] && <TypingIndicator />}

      {chatState.showDeclinedMessages[1] && (
        <ChatMessage sender={TEXT_CONTENT.chat.claire} message={TEXT_CONTENT.chat.declined.message2} />
      )}

      <div ref={bottomRef} />
    </div>
  </div>

  {/* Verification Dialog */}
  <Dialog
    open={openVerification}
    onClose={handleVerificationClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      style: {
        backgroundColor: "white",
        color: "black",
        borderRadius: "12px",
        padding: "20px",
      },
    }}
  >
    <DialogTitle className="text-center text-xl font-bold text-gray-800">
      {TEXT_CONTENT.ui.verificationTitle}
    </DialogTitle>

    <DialogContent>
      <div className="space-y-4 mt-4">
        {errors.submit && (
          <Alert severity="error" className="mb-4">
            {errors.submit}
          </Alert>
        )}

        {/* All fields unchanged */}
        <TextField
          fullWidth
          label={TEXT_CONTENT.form.labels.name}
          name="name"
          value={verificationForm.name}
          onChange={handleInputChange}
          variant="outlined"
          error={!!formErrors.name}
          helperText={formErrors.name}
          InputProps={{ className: "text-gray-800" }}
          InputLabelProps={{ className: "text-gray-600" }}
          required
          disabled={isSubmitting}
        />

        <TextField
          fullWidth
          label={TEXT_CONTENT.form.labels.number}
          name="number"
          value={verificationForm.number}
          onChange={handleInputChange}
          variant="outlined"
          error={!!formErrors.number}
          helperText={formErrors.number}
          placeholder={TEXT_CONTENT.form.placeholders.number}
          InputProps={{ className: "text-gray-800" }}
          InputLabelProps={{ className: "text-gray-600" }}
          required
          disabled={isSubmitting}
        />

        <TextField
          fullWidth
          label={TEXT_CONTENT.form.labels.email}
          name="email"
          type="email"
          value={verificationForm.email}
          onChange={handleInputChange}
          variant="outlined"
          error={!!formErrors.email}
          helperText={formErrors.email}
          InputProps={{ className: "text-gray-800" }}
          InputLabelProps={{ className: "text-gray-600" }}
          required
          disabled={isSubmitting}
        />

        <TextField
          fullWidth
          label="ZIP Code"
          name="zipcode"
          value={verificationForm.zipcode}
          onChange={handleInputChange}
          variant="outlined"
          error={!!formErrors.zipcode}
          helperText={formErrors.zipcode}
          InputProps={{ className: "text-gray-800" }}
          InputLabelProps={{ className: "text-gray-600" }}
          required
          disabled={isSubmitting}
        />

        <TextField
          fullWidth
          label="Address"
          name="address"
          value={verificationForm.address}
          onChange={handleInputChange}
          variant="outlined"
          error={!!formErrors.address}
          helperText={formErrors.address}
          InputProps={{ className: "text-gray-800" }}
          InputLabelProps={{ className: "text-gray-600" }}
          required
          disabled={isSubmitting}
        />
      </div>
    </DialogContent>

    <DialogActions className="justify-center pb-4">
      <Button
        onClick={handleVerificationSubmit}
        variant="contained"
        color="primary"
        className="bg-green-600 hover:bg-green-700 text-white"
        disabled={isSubmitting}
      >
        {isSubmitting ? TEXT_CONTENT.ui.submitting : TEXT_CONTENT.ui.submit}
      </Button>
    </DialogActions>
  </Dialog>
</div>

  )
}